import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
} from '@mui/material';
import { CloseOutlined } from '@mui/icons-material';
import { Library } from '@userpath/constants';
import { Lang } from '@userpath/localization';
import { ArticleContent } from '@userpath/components';
import { ConfigOverrider } from '@userpath/utils';
import { ArticleDTO, ContentDTO } from '@userpath/types';
import { Styled } from './styles';
import { Device } from './Device';

interface IPreviewContentProps {
  article: ArticleDTO;
  contentEnglish: ContentDTO;
  contentArabic: ContentDTO;
  open?: boolean;
  onClose: () => void;
}

const PreviewContent: React.FC<IPreviewContentProps> = ({
  article,
  contentEnglish,
  contentArabic,
  open = false,
  onClose,
}) => {
  const { t: ot, i18n } = useTranslation(Library.userpath);
  const [isMobile, setIsMobile] = useState(false);
  const [isArabic, setIsArabic] = useState(i18n.language == Lang.AR);
  const updateLanguage = useCallback(() => {
    setIsArabic((old) => !old);
  }, []);
  const updateView = useCallback(() => {
    setIsMobile((old) => !old);
  }, []);

  return (
    <Styled.Dialog
      open={open}
      maxWidth="xl"
      onClose={onClose}
      aria-labelledby="preview-content-title"
    >
      <DialogTitle id="preview-content-title">
        {ot('admin.article.previewArticle')}
      </DialogTitle>
      <IconButton
        aria-label="close"
        onClick={onClose}
        sx={{
          position: 'absolute',
          right: 8,
          top: 8,
          color: (theme) => theme.palette.grey[500],
        }}
      >
        <CloseOutlined />
      </IconButton>
      <DialogContent dividers>
        <ConfigOverrider
          lang={isArabic ? Lang.AR : Lang.EN}
          forceBreakpoint={isMobile ? 'xs' : 'md'}
        >
          <Device device={isMobile ? 'mobile' : 'desktop'}>
            <Styled.Container
              bgcolor={(theme) => theme.palette.background.paper}
              isArabic={isArabic}
            >
              <ArticleContent
                disableInteractions
                article={article}
                content={isArabic ? contentArabic : contentEnglish}
              />
            </Styled.Container>
          </Device>
        </ConfigOverrider>
      </DialogContent>
      <DialogActions>
        <Button onClick={updateLanguage}>
          {ot(isArabic ? 'admin.article.english' : 'admin.article.arabic')}
        </Button>
        <Button onClick={updateView}>
          {ot(isMobile ? 'admin.article.desktop' : 'admin.article.mobile')}
        </Button>
      </DialogActions>
    </Styled.Dialog>
  );
};

export default PreviewContent;
